import { useMemo, useState } from 'react';
import { imageCandidates } from '@/lib/imageCandidates';

/**
 * An image that degrades into a labelled placeholder.
 *
 * Drop a file at `apps/web/public/images/<name>` and it appears. Until then
 * the slot shows the exact filename and pixel size it wants, so the layout is
 * final before the photography exists and nothing shifts when it arrives.
 *
 * The extension is flexible — .jpg, .png and .webp all work for the same
 * basename, so nobody has to convert files before they can see them.
 *
 * Specs for every slot: docs/04-IMAGE-SPEC.md
 */
interface ImageSlotProps {
  /** Basename inside apps/web/public/images/ — e.g. "role-consumer.jpg" */
  name: string;
  alt: string;
  /** Intrinsic width in CSS pixels. Source file should be 2× this. */
  width: number;
  height: number;
  className?: string;
  /** Skip lazy-loading for anything above the fold. */
  priority?: boolean;
  /** Object-position, for art-directing a crop. */
  position?: string;
}

export default function ImageSlot({
  name,
  alt,
  width,
  height,
  className = '',
  priority = false,
  position = 'center',
}: ImageSlotProps) {
  const candidates = useMemo(() => imageCandidates(name), [name]);
  const [attempt, setAttempt] = useState(0);

  const src = candidates[attempt];
  const failed = src === undefined;

  if (failed) {
    return (
      <div
        className={`relative flex flex-col items-center justify-center overflow-hidden ${className}`}
        style={{
          aspectRatio: `${width} / ${height}`,
          background:
            'repeating-linear-gradient(135deg, var(--surface-2) 0 12px, var(--surface-3) 12px 24px)',
          border: '1px dashed var(--line)',
        }}
        role="img"
        aria-label={`Placeholder for ${alt}`}
      >
        <svg viewBox="0 0 24 24" className="size-7 text-[var(--muted)]" fill="none" aria-hidden>
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="8.5" cy="9.5" r="1.6" fill="currentColor" />
          <path
            d="M3.5 17l4.8-4.6a1.6 1.6 0 012.2 0l3 2.9m0 0l2-1.9a1.6 1.6 0 012.2 0l2.8 2.7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <p className="mt-2.5 px-3 text-center font-[family-name:var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)]">
          {name}
        </p>
        <p className="mt-0.5 font-[family-name:var(--font-mono)] text-[11px] text-[var(--muted)] tabular-nums">
          {width * 2} × {height * 2} px
        </p>
      </div>
    );
  }

  return (
    <img
      src={`/images/${src}`}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      // Try the next extension; once they are exhausted the placeholder shows.
      onError={() => setAttempt((i) => i + 1)}
      className={`object-cover ${className}`}
      style={{ aspectRatio: `${width} / ${height}`, objectPosition: position }}
    />
  );
}
